import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { ChevronDown, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { AgentActivityRowCard } from "@/components/agent/agent-activity-row"
import { Button } from "@/components/ui/primitives"
import {
  agentActivityScopeKey,
  fetchAgentActivity,
  markAgentActivityViewed,
  setAgentActivityPinned,
  type AgentActivityResponse,
  type AgentActivityRow,
  type AgentActivityScope,
} from "@/lib/agent-activity-api"
import { confirm } from "@/lib/confirm"
import { visitAgentSurface, visitWithShellNav } from "@/lib/open-workspace-agent"
import { useAgentActivityStore } from "@/stores/agent-activity-store"
import { useShotPanelStore } from "@/stores/shot-panel-store"
import { useUiStore } from "@/stores/ui-store"

type AgentActivityDrawerProps = {
  scope: AgentActivityScope | null
  theme: "dark" | "light"
  /** Bump to force refetch (cable events). */
  refreshNonce?: number
}

const EMPTY: AgentActivityResponse = {
  pinned: [],
  ready_unread: [],
  in_progress: [],
  ready_viewed: [],
  inactive: { items: [], page: 1, per_page: 20, total: 0, has_more: false },
  counts: { ready_unread: 0, in_progress: 0, ready_viewed: 0, inactive: 0 },
}

function normalizeActivityResponse(next: AgentActivityResponse): AgentActivityResponse {
  return {
    ...next,
    pinned: next.pinned ?? [],
  }
}

function navigateActivitySlot(scope: AgentActivityScope, row: AgentActivityRow) {
  const link = row.deep_link
  switch (link.surface) {
    case "company_canvas":
      useShotPanelStore.getState().close()
      if (link.entityId && link.entityId > 0) {
        visitWithShellNav(`/company/canvas?canvas_id=${link.entityId}`)
      } else {
        visitWithShellNav("/company/canvas")
      }
      return
    case "company_skills":
      useShotPanelStore.getState().close()
      visitWithShellNav("/company/skills")
      return
    case "company_projects":
      useShotPanelStore.getState().close()
      visitWithShellNav("/projects")
      return
    case "shot":
      if (scope.kind !== "project") return
      if (link.shotId && link.shotId > 0) {
        useShotPanelStore.getState().openShotPanel({ projectId: scope.id, shotId: link.shotId, view: "preview" })
      }
      return
    case "characters":
    case "locations":
    case "props":
      if (scope.kind !== "project") return
      visitAgentSurface({
        projectId: scope.id,
        surface: link.surface,
        entityId: link.entityId,
      })
      return
    case "canvas":
      if (scope.kind !== "project") return
      useShotPanelStore.getState().close()
      if (link.entityId && link.entityId > 0) {
        visitWithShellNav(`/projects/${scope.id}/workspace/canvas?canvas_id=${link.entityId}`)
      } else {
        visitWithShellNav(`/projects/${scope.id}/workspace/canvas`)
      }
      return
    case "script":
      if (scope.kind !== "project") return
      useShotPanelStore.getState().close()
      visitWithShellNav(`/projects/${scope.id}/workspace/script`)
      return
    case "scenes3d":
      if (scope.kind !== "project") return
      useShotPanelStore.getState().close()
      if (link.entityId && link.entityId > 0) {
        visitWithShellNav(`/projects/${scope.id}/workspace/scenes_3d/${link.entityId}`)
      } else {
        visitWithShellNav(`/projects/${scope.id}/workspace/scenes_3d`)
      }
      return
    case "skills":
      if (scope.kind !== "project") return
      useShotPanelStore.getState().close()
      visitWithShellNav(`/projects/${scope.id}/workspace/skills`)
      return
    case "shots":
    default:
      if (scope.kind !== "project") return
      useShotPanelStore.getState().close()
      visitWithShellNav(`/projects/${scope.id}/workspace/storyboard`)
  }
}

function Section({
  title,
  rows,
  theme,
  selectedSlotKey,
  pinnedSlotKeys,
  onOpen,
  onPinToggle,
}: {
  title: string
  rows: AgentActivityRow[]
  theme: "dark" | "light"
  selectedSlotKey: string | null
  pinnedSlotKeys: Set<string>
  onOpen: (row: AgentActivityRow) => void
  onPinToggle: (row: AgentActivityRow, pinned: boolean) => void
}) {
  const reduceMotion = useReducedMotion()
  if (rows.length === 0) return null

  return (
    <div>
      <p className="mb-2 px-1.5 text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">{title}</p>
      <div className="space-y-2">
        {rows.map((row) => (
          <motion.div
            key={row.slot_key}
            layout={!reduceMotion}
            transition={{ type: "spring", stiffness: 420, damping: 36, mass: 0.8 }}
          >
            <AgentActivityRowCard
              row={row}
              theme={theme}
              selected={row.slot_key === selectedSlotKey}
              pinned={pinnedSlotKeys.has(row.slot_key)}
              onOpen={onOpen}
              onPinToggle={onPinToggle}
            />
          </motion.div>
        ))}
      </div>
    </div>
  )
}

/**
 * Reuse: section/card chrome mirrors jobs_history in AppShell; open via visitAgentSurface /
 * shot panel; busy via Loader2 (same as jobs drawer + AgentHandoffChip). Pin chrome like
 * project-card Star; unpin via confirm().
 */
export function AgentActivityDrawer({ scope, theme, refreshNonce = 0 }: AgentActivityDrawerProps) {
  const { t } = useTranslation("agentActivity")
  const [data, setData] = useState<AgentActivityResponse>(EMPTY)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedRow, setSelectedRow] = useState<AgentActivityRow | null>(null)
  const selectedRowRef = useRef<AgentActivityRow | null>(null)
  selectedRowRef.current = selectedRow
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const mountedRef = useRef(true)
  const loadSeqRef = useRef(0)
  const scopeKey = scope ? agentActivityScopeKey(scope) : null

  const load = useCallback(
    async (page = 1, appendInactive = false) => {
      if (!scope) return
      const seq = ++loadSeqRef.current
      if (appendInactive) setLoadingMore(true)
      else setLoading(true)
      setError(null)
      try {
        const next = normalizeActivityResponse(await fetchAgentActivity(scope, page))
        if (seq !== loadSeqRef.current) return
        useAgentActivityStore.getState().setReadyUnreadCount(scope, next.counts.ready_unread)
        if (!mountedRef.current) return
        setData((prev) => {
          if (!appendInactive) return next
          return {
            ...next,
            pinned: next.pinned,
            ready_unread: next.ready_unread,
            in_progress: next.in_progress,
            ready_viewed: next.ready_viewed,
            inactive: {
              ...next.inactive,
              items: [...prev.inactive.items, ...next.inactive.items],
            },
          }
        })
      } catch {
        if (seq === loadSeqRef.current && mountedRef.current) setError(t("loadError"))
      } finally {
        if (seq === loadSeqRef.current && mountedRef.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [scope, t],
  )

  // Mark + refresh badge via store (safe after unmount). Does not touch React state.
  const markViewedRow = useCallback(async (row: AgentActivityRow | null) => {
    const activeScope = scopeRef.current
    if (!activeScope || !row || row.status_bucket !== "ready_unread") return
    try {
      await markAgentActivityViewed({
        scope: activeScope,
        mode: row.mode,
        entityKind: row.entity_kind,
        entityId: row.entity_id,
      })
      const seq = ++loadSeqRef.current
      const next = await fetchAgentActivity(activeScope, 1)
      if (seq !== loadSeqRef.current) return
      useAgentActivityStore.getState().setReadyUnreadCount(activeScope, next.counts.ready_unread)
    } catch {
      /* best-effort mark/badge */
    }
  }, [])

  const commitViewedIfNeeded = useCallback(
    async (row: AgentActivityRow | null) => {
      await markViewedRow(row)
      if (mountedRef.current) await load(1, false)
    },
    [markViewedRow, load],
  )

  const openActivitySlot = useCallback(
    (row: AgentActivityRow) => {
      if (!scope) return
      const prev = selectedRowRef.current
      setSelectedRow(row)
      useUiStore.getState().setWorkspaceBottomDockCollapsed(false)
      navigateActivitySlot(scope, row)
      if (prev && prev.slot_key !== row.slot_key) void commitViewedIfNeeded(prev)
    },
    [scope, commitViewedIfNeeded],
  )

  const handlePinToggle = useCallback(
    async (row: AgentActivityRow, nextPinned: boolean) => {
      if (!scope) return
      if (!nextPinned) {
        const ok = await confirm({ message: t("unpinConfirm") })
        if (!ok) return
      }
      try {
        await setAgentActivityPinned({
          scope,
          slotKey: row.slot_key,
          pinned: nextPinned,
        })
        if (mountedRef.current) await load(1, false)
      } catch {
        /* best-effort pin */
      }
    },
    [scope, t, load],
  )

  useEffect(() => {
    // Other scope — drop selection without marking viewed; clear list so first load shows spinner.
    setSelectedRow(null)
    setData(EMPTY)
    setError(null)
  }, [scopeKey])

  // Leave card: outside Agents drawer → mark ready_unread if needed, then clear selection.
  useEffect(() => {
    if (!selectedRow) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target
      if (!(target instanceof Element)) return
      if (target.closest("[data-shell-drawer]")) return
      const leaving = selectedRowRef.current
      setSelectedRow(null)
      void commitViewedIfNeeded(leaving)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [selectedRow, commitViewedIfNeeded])

  // Tab leave / drawer close unmounts this component — mark selected ready_unread.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      void markViewedRow(selectedRowRef.current)
    }
  }, [markViewedRow])

  useEffect(() => {
    void load(1, false)
  }, [load, refreshNonce])

  const pinnedRows = useMemo(() => data.pinned ?? [], [data.pinned])
  const pinnedSlotKeys = useMemo(() => new Set(pinnedRows.map((row) => row.slot_key)), [pinnedRows])

  const activeRows = useMemo(() => [...data.in_progress, ...data.ready_unread], [data.in_progress, data.ready_unread])

  if (!scope) {
    return <p className="text-xs text-ui-text-muted">{t("openProject")}</p>
  }

  // Full-page spinner/error only when empty — keep stale rows on cable refetch.
  const hasListContent =
    pinnedRows.length > 0 ||
    data.ready_unread.length > 0 ||
    data.in_progress.length > 0 ||
    data.ready_viewed.length > 0 ||
    data.inactive.items.length > 0

  if (loading && !hasListContent) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
      </div>
    )
  }

  if (error && !hasListContent) {
    return <p className="text-xs text-ui-status-failed">{error}</p>
  }

  const totalActive =
    pinnedRows.length +
    data.counts.ready_unread +
    data.counts.in_progress +
    data.counts.ready_viewed +
    data.counts.inactive

  const selectedSlotKey = selectedRow?.slot_key ?? null
  const openRow = (r: AgentActivityRow) => void openActivitySlot(r)

  return (
    // -mx-5 cancels drawer body p-5; px-1.5 = gap panel→highlight (same as row p-1.5);
    // titles + row use px-1.5 / p-1.5 so thumb aligns with headers and pad is equal T/B/L/R.
    <div className="-mx-5 space-y-5 px-1.5">
      {totalActive === 0 ? <p className="px-1.5 text-xs text-ui-text-muted">{t("empty")}</p> : null}

      <Section
        title={t("pinned")}
        rows={pinnedRows}
        theme={theme}
        selectedSlotKey={selectedSlotKey}
        pinnedSlotKeys={pinnedSlotKeys}
        onOpen={openRow}
        onPinToggle={(row, pinned) => void handlePinToggle(row, pinned)}
      />
      <Section
        title={t("active")}
        rows={activeRows}
        theme={theme}
        selectedSlotKey={selectedSlotKey}
        pinnedSlotKeys={pinnedSlotKeys}
        onOpen={openRow}
        onPinToggle={(row, pinned) => void handlePinToggle(row, pinned)}
      />
      <Section
        title={t("read")}
        rows={data.ready_viewed}
        theme={theme}
        selectedSlotKey={selectedSlotKey}
        pinnedSlotKeys={pinnedSlotKeys}
        onOpen={openRow}
        onPinToggle={(row, pinned) => void handlePinToggle(row, pinned)}
      />
      <Section
        title={t("inactive")}
        rows={data.inactive.items}
        theme={theme}
        selectedSlotKey={selectedSlotKey}
        pinnedSlotKeys={pinnedSlotKeys}
        onOpen={openRow}
        onPinToggle={(row, pinned) => void handlePinToggle(row, pinned)}
      />

      {data.inactive.has_more ? (
        <div className="flex justify-center px-1.5 pt-1">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={loadingMore}
            onClick={() => void load(data.inactive.page + 1, true)}
          >
            {loadingMore ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ChevronDown className="mr-1 h-3 w-3" />}
            {t("loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
